'use client';

import { Suspense, useCallback } from 'react';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@shared/hooks/use-router';
import { LogoAnimated } from '@/components/common/logo-animated';

import { APP_ENVS } from '@/shared/constants/envs';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES } from '@/shared/constants/navigation';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { redirectViaWindow } from '@/shared/utils/window';

import { MARKETING_PAGE, trackGetStartedClicked } from '../../utils/marketing-analytics';

function LoginLink() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const qs = searchParams?.toString?.() ?? '';
	const loginHref = qs ? `${APP_ROUTES.LOGIN}?${qs}` : APP_ROUTES.LOGIN;

	const handleLoginClick = useCallback(() => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_LANDING_PAGE_SIGN_IN, {
			feature: MIXPANEL_APP_FEATURES.LANDING_PAGE,
		});
		router.push(loginHref);
	}, [router, loginHref]);

	return (
		<Link
			href={loginHref}
			className="group flex items-center justify-center py-2.5 text-sm text-secondary transition-colors hover:text-primary md:text-base"
			onClick={handleLoginClick}
		>
			<span className="inline-block">Log in</span>
		</Link>
	);
}

export const HeaderSection = ({ page }: { page: MARKETING_PAGE }) => {
	const handleStartFreeClick = useCallback(() => {
		trackGetStartedClicked(page, 'header');
		redirectViaWindow(APP_ROUTES.LOGIN);
	}, [page]);

	return (
		<header className="sticky top-0 z-50 w-full bg-[#F8F8F8]">
			<div className="landing-width flex items-center justify-between px-4 py-4">
				<Link
					href={APP_ROUTES.ROOT}
					aria-label="Go to homepage"
				>
					<LogoAnimated />
				</Link>
				<div className="flex flex-1 items-center justify-end gap-5 text-sm text-secondary md:gap-6">
					{APP_ENVS.BOOK_DEMO_URL && (
						<Link
							href={APP_ENVS.BOOK_DEMO_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="group hidden items-center justify-center py-2.5 text-sm text-secondary transition-colors hover:text-primary md:flex md:text-base"
						>
							<span className="inline-block">Book a demo</span>
						</Link>
					)}

					<Link
						href={APP_ROUTES.FREE_ATS}
						className="group hidden items-center justify-center py-2.5 text-sm text-secondary transition-colors hover:text-primary md:flex md:text-base"
					>
						<span className="inline-block">Free ATS</span>
					</Link>

					<Link
						href={APP_ROUTES.PRICING}
						className="group flex items-center justify-center py-2.5 text-sm text-secondary transition-colors hover:text-primary md:text-base"
						onClick={() =>
							emitMixpanelEvent(mixpanelEvents.CLICKED_LANDING_PAGE_PRICING, {
								feature: MIXPANEL_APP_FEATURES.LANDING_PAGE,
							})
						}
					>
						<span className="inline-block">Pricing</span>
					</Link>

					<Suspense
						fallback={
							<Link
								href={APP_ROUTES.LOGIN}
								className="group flex items-center justify-center py-2.5 text-sm text-secondary transition-colors hover:text-primary md:text-base"
							>
								<span className="inline-block">Log in</span>
							</Link>
						}
					>
						<LoginLink />
					</Suspense>

					<button
						onClick={handleStartFreeClick}
						className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition-all hover:bg-primary/90 md:text-base"
					>
						Start for free
					</button>
				</div>
			</div>
		</header>
	);
};
